import { useContentfulClient } from "@expert/contentful";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { getChallengesForEmployee, type QuizChallenge } from "@/sales-tools/challenges/api/getChallengesForEmployee";
import { useLogger, usePartner } from "@/sdk";

import type { Question } from "../types";

import { determineRandomQuestion, getSingleQuestionnaireQuestion } from "../components/api/questionnaire";
import { getCorrectAnswers, getTopic, getTotalQuestions, isQuizChallenge, isQuizDone } from "../utils";

const queryKey = ["next-question"];

type UseQuizChallengeData =
    | {
          quiz: QuizChallenge;
          question: Question;
          topic: string;
          totalQuestions: number;
          correctAnswers: number;
          isQuizDone: boolean;
      }
    | undefined;

export function useQuizChallenge() {
    const logger = useLogger({ module: "useQuizChallenge", supportTeam: "sales" });

    const queryClient = useQueryClient();
    const contentfulClient = useContentfulClient();
    const partner = usePartner();

    const { data, isLoading, isError } = useQuery<UseQuizChallengeData>({
        queryKey,
        queryFn: async () => {
            const challenges = await getChallengesForEmployee(logger, partner);
            const quiz = challenges?.find(isQuizChallenge);

            if (quiz == null) {
                throw new Error("Cannot find quiz");
            }

            const question = await getSingleQuestionnaireQuestion(
                logger,
                determineRandomQuestion(quiz),
                contentfulClient,
            );

            if (question == null) {
                throw new Error("Cannot find question");
            }

            const topic = getTopic(quiz, question);

            if (topic == null) {
                throw new Error("Cannot find topic");
            }

            const totalQuestions = getTotalQuestions(quiz);
            const correctAnswers = getCorrectAnswers(quiz);

            const { choices } = question;

            if (choices.find((choice) => choice.confirmationText)) {
                // confirmation text is only present on true-false questions
                // always show "True" first
                choices.sort((a) => (a.choice === "True" ? -1 : 1));
            } else {
                for (let i = choices.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [choices[i], choices[j]] = [choices[j], choices[i]];
                }
            }

            return {
                quiz,
                question,
                topic,
                totalQuestions,
                correctAnswers,
                isQuizDone: isQuizDone(quiz, totalQuestions, correctAnswers),
            };
        },
        retry: 1,
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const invalidateQuizQuestion = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey, refetchType: "none" });
    }, [queryClient]);

    const setCorrectAnswers = useCallback(
        (count: number) => {
            queryClient.setQueryData<UseQuizChallengeData>(queryKey, (data) => {
                if (data == null) return data;

                return {
                    ...data,
                    correctAnswers: count,
                };
            });
        },
        [queryClient],
    );

    return { data, isLoading, isError, invalidateQuizQuestion, setCorrectAnswers };
}
